import Phaser from 'phaser';

// Horse color variants with tint values and spawn chances
export interface HorseVariant {
  name: string;
  displayName: string;  // For UI display
  bodyTint: number;
  chance: number;       // Spawn weight (0-100)
  isPaint: boolean;     // Whether this is a paint pattern version
}

// Base coat colors - these can all have paint versions when unlocked
export const BASE_COATS = [
  { name: 'grulla', displayName: 'Grulla', bodyTint: 0xA8A8A8, chance: 25 },
  { name: 'buckskin', displayName: 'Buckskin', bodyTint: 0xD2B48C, chance: 20 },
  { name: 'dun', displayName: 'Dun', bodyTint: 0xD2B48C, chance: 15 },
  { name: 'bay', displayName: 'Bay', bodyTint: 0x8B4513, chance: 15 },
  { name: 'sorrel', displayName: 'Sorrel', bodyTint: 0xB22222, chance: 10 },
  { name: 'roan', displayName: 'Roan', bodyTint: 0xC8B8B8, chance: 5 },
  { name: 'black', displayName: 'Black', bodyTint: 0x1A1A1A, chance: 5 },
  { name: 'rose_grey', displayName: 'Rose Grey', bodyTint: 0xD8C8D0, chance: 5 },
];

// Build full variant list (base coats + paint versions when unlocked)
export function getHorseVariants(): HorseVariant[] {
  const variants: HorseVariant[] = [];
  
  // Add all base coats
  for (const coat of BASE_COATS) {
    variants.push({
      name: coat.name,
      displayName: coat.displayName,
      bodyTint: coat.bodyTint,
      chance: coat.chance,
      isPaint: false
    });
  }
  
  // Add paint versions if unlocked (20% chance to be paint version)
  if (isPaintUnlocked()) {
    for (const coat of BASE_COATS) {
      variants.push({
        name: `${coat.name}_paint`,
        displayName: `${coat.displayName} Paint`,
        bodyTint: coat.bodyTint,
        chance: coat.chance * 0.2, // 20% of base chance
        isPaint: true
      });
    }
  }
  
  return variants;
}

// Check if paint horses are unlocked (based on reputation threshold)
export function isPaintUnlocked(): boolean {
  try {
    return localStorage.getItem('pmp_paint_unlocked') === 'true';
  } catch {
    return false;
  }
}

export function unlockPaint(): void {
  try {
    localStorage.setItem('pmp_paint_unlocked', 'true');
    console.log('🎨 Paint horses unlocked!');
  } catch {
    // Storage not available
  }
}

// Check if rose grey is unlocked
export function isRoseGreyUnlocked(): boolean {
  try {
    return localStorage.getItem('pmp_rose_grey_unlocked') === 'true';
  } catch {
    return false;
  }
}

export function unlockRoseGrey(): void {
  try {
    localStorage.setItem('pmp_rose_grey_unlocked', 'true');
    console.log('🌸 Rose Grey horses unlocked!');
  } catch {
    // Storage not available
  }
}

// Social interaction emojis
const SOCIAL_EMOJIS = ['❤️', '💕', '😊', '🥰', '✨', '💫', '🌟', '😄'];

export type TimeOfDay = 'morning' | 'day' | 'night';
export type HorseGender = 'male' | 'female';
export type HorseAgeCategory = 'foal' | 'yearling' | 'adult';

export class HorseSprite extends Phaser.GameObjects.Container {
  private bodySprite: Phaser.GameObjects.Image;
  private wanderTween?: Phaser.Tweens.Tween;
  private bobTween?: Phaser.Tweens.Tween;
  private popTween?: Phaser.Tweens.Tween;
  private isGrazing: boolean = false;
  private variant: HorseVariant;
  private currentTimeOfDay: TimeOfDay = 'day';
  private baseTint: number;
  private interactionTimer?: Phaser.Time.TimerEvent;
  
  // Age and gender system
  public gender: HorseGender;
  public birthYear: number;
  private hoverText?: Phaser.GameObjects.Text;
  private isPaused: boolean = false;

  constructor(scene: Phaser.Scene, x: number, y: number, variantName?: string, forcePaint?: boolean, birthYear?: number, gender?: HorseGender) {
    super(scene, x, y);
    
    // Assign gender (random if not specified)
    this.gender = gender || (Math.random() < 0.5 ? 'male' : 'female');
    
    // Assign birth year (default to current year - random age 0-10)
    this.birthYear = birthYear || 1990 - Math.floor(Math.random() * 10);
    
    // Pick variant - either specified or random
    if (variantName) {
      // Handle special color names - check for paint suffix
      const normalizedName = variantName.toLowerCase();
      const isPaintRequest = normalizedName === 'paint' || normalizedName.includes('paint') || forcePaint;
      const baseColorName = normalizedName.replace('_paint', '').replace(' paint', '').replace('paint', '').trim();
      
      // If explicitly requesting paint (no base color), use a random base with paint
      if (isPaintRequest && (!baseColorName || baseColorName === '')) {
        const randomBase = BASE_COATS[Math.floor(Math.random() * BASE_COATS.length)];
        this.variant = {
          name: `${randomBase.name}_paint`,
          displayName: `${randomBase.displayName} Paint`,
          bodyTint: randomBase.bodyTint,
          chance: randomBase.chance,
          isPaint: true
        };
      } else {
        // Find the base coat
        const baseCoat = BASE_COATS.find(c => c.name.toLowerCase() === baseColorName);
        
        if (baseCoat) {
          // Create variant from base coat - paint if requested (no unlock check for explicit requests)
          this.variant = {
            name: isPaintRequest ? `${baseCoat.name}_paint` : baseCoat.name,
            displayName: isPaintRequest ? `${baseCoat.displayName} Paint` : baseCoat.displayName,
            bodyTint: baseCoat.bodyTint,
            chance: baseCoat.chance,
            isPaint: isPaintRequest || false
          };
        } else {
          // Fallback to random
          this.variant = this.pickVariant();
        }
      }
    } else {
      this.variant = this.pickVariant();
    }
    this.baseTint = this.variant.bodyTint;
    
    // Determine initial state: 70% idle, 30% eating
    const isEating = Math.random() < 0.3;
    
    // Use paint sprite if this is a paint variant
    const usePaintSprite = this.variant.isPaint;
    // Note: texture keys are 'paint1-idle', 'paint1-eat', 'grulla-idle', 'grulla-eat'
    const textureKey = usePaintSprite 
      ? (isEating ? 'paint1-eat' : 'paint1-idle')
      : (isEating ? 'grulla-eat' : 'grulla-idle');
    
    // Create body sprite - ORIGINAL SIZE (no scale)
    this.bodySprite = scene.add.image(0, 0, textureKey);
    
    // Apply tint - ALL horses get tinted, including paints!
    this.bodySprite.setTint(this.variant.bodyTint);
    this.add(this.bodySprite);
    
    // Keep original sprite size - no scaling
    this.setScale(1);
    
    // Set depth based on Y for proper layering (keep horses under cards at depth 500+)
    // Horses are in Y range 420-520, so depth 40-52 keeps them well below cards
    this.setDepth(Math.floor(y / 10));
    
    scene.add.existing(this);
    
    // Start behavior
    if (isEating) {
      this.isGrazing = true;
      this.graze();
    } else {
      this.wander();
    }
    
    // Start subtle pop animation
    this.startPopAnimation();
    
    // Schedule random social interactions
    this.scheduleInteraction();
  }

  private pickVariant(): HorseVariant {
    // Get current variants (includes paint versions if unlocked)
    const variants = getHorseVariants();
    
    // Calculate total chance
    const totalChance = variants.reduce((sum, v) => sum + v.chance, 0);
    const roll = Math.random() * totalChance;
    let cumulative = 0;
    
    for (const variant of variants) {
      cumulative += variant.chance;
      if (roll < cumulative) {
        return variant;
      }
    }
    
    // Default to first base coat (Grulla)
    return {
      name: BASE_COATS[0].name,
      displayName: BASE_COATS[0].displayName,
      bodyTint: BASE_COATS[0].bodyTint,
      chance: BASE_COATS[0].chance,
      isPaint: false
    };
  }
  
  // Get age in years based on current game year
  getAge(currentYear: number): number {
    return currentYear - this.birthYear;
  }
  
  // Get age category
  getAgeCategory(currentYear: number): HorseAgeCategory {
    const age = this.getAge(currentYear);
    if (age < 1) return 'foal';
    if (age < 2) return 'yearling';
    return 'adult';
  }
  
  // Get display name (filly, colt, mare, stallion)
  getDisplayName(currentYear: number): string {
    const age = this.getAge(currentYear);
    if (age < 2) {
      return this.gender === 'female' ? 'Filly' : 'Colt';
    }
    return this.gender === 'female' ? 'Mare' : 'Stallion';
  }
  
  // Get full info string for hover
  getInfoString(currentYear: number): string {
    const age = this.getAge(currentYear);
    const name = this.getDisplayName(currentYear);
    return `${name}, ${age}yr`;
  }
  
  // Update scale based on age (foals are smaller)
  updateScaleForAge(currentYear: number): void {
    const age = this.getAge(currentYear);
    if (age < 2) {
      this.setScale(0.6); // Foals and yearlings are smaller
    } else {
      this.setScale(1);
    }
  }
  
  // Age up the horse (called at year end)
  ageUp(currentYear: number): void {
    this.updateScaleForAge(currentYear);
  }
  
  // Pause movement for adopt mode
  pauseMovement(): void {
    this.isPaused = true;
    // Stop current wander tween
    if (this.wanderTween) {
      this.wanderTween.pause();
    }
    if (this.popTween) {
      this.popTween.pause();
    }
  }
  
  // Resume movement after adopt mode
  resumeMovement(): void {
    this.isPaused = false;
    // Resume tweens
    if (this.wanderTween) {
      this.wanderTween.resume();
    }
    if (this.popTween) {
      this.popTween.resume();
    }
  }
  
  // Subtle pop up/down animation
  private startPopAnimation(): void {
    if (!this.scene || !this.active) return;
    
    this.popTween = this.scene.tweens.add({
      targets: this,
      y: this.y - 3,
      duration: Phaser.Math.Between(1500, 2500),
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut',
      delay: Phaser.Math.Between(0, 1000)
    });
  }
  
  // Schedule random social interactions - RARE (1-2 minutes)
  private scheduleInteraction(): void {
    if (!this.scene || !this.active) return;
    
    // Interact every 60-120 seconds (1-2 minutes)
    this.interactionTimer = this.scene.time.delayedCall(
      Phaser.Math.Between(60000, 120000),
      () => {
        if (this.active) {
          this.showSocialEmoji();
          this.scheduleInteraction();
        }
      }
    );
  }
  
  // Show emoji above horse head
  private showSocialEmoji(): void {
    if (!this.scene || !this.active) return;
    
    const emoji = Phaser.Math.RND.pick(SOCIAL_EMOJIS);
    const emojiText = this.scene.add.text(this.x, this.y - 30, emoji, {
      fontSize: '20px'
    }).setOrigin(0.5).setDepth(1000);
    
    // Float up and fade
    this.scene.tweens.add({
      targets: emojiText,
      y: emojiText.y - 40,
      alpha: 0,
      scale: 1.3,
      duration: 1500,
      ease: 'Sine.easeOut',
      onComplete: () => emojiText.destroy()
    });
  }

  setTimeOfDay(time: TimeOfDay): void {
    this.currentTimeOfDay = time;
    this.updateTint();
  }

  private updateTint(): void {
    let tint = this.baseTint;
    
    switch (this.currentTimeOfDay) {
      case 'morning':
        // Warm yellow tint overlay
        tint = this.blendTint(this.baseTint, 0xFFE4B5, 0.15);
        break;
      case 'night':
        // Cool blue shadow tint
        tint = this.blendTint(this.baseTint, 0x191970, 0.35);
        break;
      case 'day':
      default:
        tint = this.baseTint;
        break;
    }
    
    this.bodySprite.setTint(tint);
  }

  private blendTint(base: number, overlay: number, amount: number): number {
    const br = (base >> 16) & 0xFF;
    const bg = (base >> 8) & 0xFF;
    const bb = base & 0xFF;
    
    const or = (overlay >> 16) & 0xFF;
    const og = (overlay >> 8) & 0xFF;
    const ob = overlay & 0xFF;
    
    const r = Math.floor(br + (or - br) * amount);
    const g = Math.floor(bg + (og - bg) * amount);
    const b = Math.floor(bb + (ob - bb) * amount);
    
    return (r << 16) | (g << 8) | b;
  }

  wander(): void {
    if (!this.scene || !this.active) return;
    
    // Don't start new wander if paused (adopt mode)
    if (this.isPaused) {
      // Check again in 1 second
      this.scene.time.delayedCall(1000, () => {
        if (this.active && !this.isPaused) this.wander();
      });
      return;
    }
    
    // Switch to idle sprite - use paint sprite if this is a paint variant
    const usePaintSprite = this.variant.isPaint;
    this.bodySprite.setTexture(usePaintSprite ? 'paint1-idle' : 'grulla-idle');
    // ALL horses get tinted, including paints
    this.bodySprite.setTint(this.variant.bodyTint);
    this.isGrazing = false;
    
    // Random chance to graze instead of walk
    if (Math.random() < 0.3) {
      this.graze();
      return;
    }
    
    // Target position - ON THE GRASS (below trees, ABOVE cards at bottom)
    // Grass area is roughly y=420 to y=520 - keep horses above card area
    const tx = Phaser.Math.Between(100, 800);
    const ty = Phaser.Math.Between(420, 520);
    
    // Flip sprite based on direction
    // Sprite faces LEFT by default, so:
    // - Moving RIGHT (tx > this.x) = flip horizontally (setFlipX true)
    // - Moving LEFT (tx < this.x) = no flip (setFlipX false)
    this.bodySprite.setFlipX(tx > this.x);
    
    // Update depth based on target Y (keep below cards at depth 500+)
    this.setDepth(Math.floor(ty / 10));
    
    // Stop any existing tweens
    if (this.wanderTween) this.wanderTween.stop();
    if (this.bobTween) this.bobTween.stop();
    
    // Walking tween
    this.wanderTween = this.scene.tweens.add({
      targets: this,
      x: tx,
      y: ty,
      duration: Phaser.Math.Between(4000, 8000),
      ease: 'Sine.easeInOut',
      onComplete: () => {
        if (this.active) {
          this.wander();
        }
      }
    });
    
    // Subtle leg bob while walking
    this.bobTween = this.scene.tweens.add({
      targets: this.bodySprite,
      y: -2,
      duration: 200,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.easeInOut'
    });
  }

  graze(): void {
    if (!this.scene || !this.active) return;
    
    // Don't start grazing if paused (adopt mode)
    if (this.isPaused) return;
    
    // Switch to eating sprite - use paint sprite if this is a paint variant
    const usePaintSprite = this.variant.isPaint;
    this.bodySprite.setTexture(usePaintSprite ? 'paint1-eat' : 'grulla-eat');
    // ALL horses get tinted, including paints
    this.bodySprite.setTint(this.variant.bodyTint);
    this.isGrazing = true;
    
    // Stop walking bob
    if (this.bobTween) {
      this.bobTween.stop();
      this.bodySprite.y = 0;
    }
    
    // Subtle head bob while eating
    this.bobTween = this.scene.tweens.add({
      targets: this.bodySprite,
      y: 2,
      duration: 800,
      yoyo: true,
      repeat: 3,
      ease: 'Sine.easeInOut'
    });
    
    // Graze for a while then wander
    this.scene.time.delayedCall(Phaser.Math.Between(3000, 6000), () => {
      if (this.active) {
        this.isGrazing = false;
        this.wander();
      }
    });
  }

  // Make horse react (jump, scatter, etc.)
  react(type: 'happy' | 'scared'): void {
    if (!this.scene || !this.active) return;
    
    if (type === 'happy') {
      // Jump animation
      this.scene.tweens.add({
        targets: this,
        y: this.y - 25,
        duration: 200,
        yoyo: true,
        ease: 'Bounce.easeOut'
      });
    } else if (type === 'scared') {
      // Scatter animation
      const scatterX = this.x + Phaser.Math.Between(-60, 60);
      this.scene.tweens.add({
        targets: this,
        x: Phaser.Math.Clamp(scatterX, 80, 820),
        duration: 400,
        ease: 'Power2'
      });
    }
  }

  getVariantName(): string {
    return this.variant.displayName;
  }
  
  isPaintVariant(): boolean {
    return this.variant.isPaint;
  }
  
  // Stallion fighting behavior - two horses rear up and clash
  stallionFight(otherHorse: HorseSprite): void {
    if (!this.scene || !this.active || !otherHorse.active) return;
    
    // Move toward each other
    const midX = (this.x + otherHorse.x) / 2;
    
    // Both horses face each other
    this.bodySprite.setFlipX(otherHorse.x > this.x);
    otherHorse.bodySprite.setFlipX(this.x > otherHorse.x);
    
    // Rear up animation (scale Y stretch)
    this.scene.tweens.add({
      targets: [this, otherHorse],
      scaleY: 1.2,
      y: '-=15',
      duration: 300,
      yoyo: true,
      repeat: 2,
      ease: 'Power2'
    });
    
    // Show fight emojis
    const fightEmojis = ['⚔️', '💢', '😤'];
    fightEmojis.forEach((emoji, i) => {
      const emojiText = this.scene.add.text(midX + (i - 1) * 20, this.y - 50, emoji, {
        fontSize: '18px'
      }).setOrigin(0.5).setDepth(1000);
      
      this.scene.tweens.add({
        targets: emojiText,
        y: emojiText.y - 40,
        alpha: 0,
        duration: 1200,
        delay: i * 200,
        onComplete: () => emojiText.destroy()
      });
    });
  }
  
  // Foal following behavior - follow a "mom" horse
  followMom(momHorse: HorseSprite): void {
    if (!this.scene || !this.active || !momHorse.active) return;
    
    // Stop current wandering
    if (this.wanderTween) this.wanderTween.stop();
    
    // Calculate position behind mom
    const offsetX = momHorse.bodySprite.flipX ? -40 : 40;
    const targetX = Phaser.Math.Clamp(momHorse.x + offsetX, 100, 800);
    const targetY = Phaser.Math.Clamp(momHorse.y + 10, 420, 520);
    
    // Face same direction as mom
    this.bodySprite.setFlipX(momHorse.bodySprite.flipX);
    
    // Move toward mom with slight delay
    this.wanderTween = this.scene.tweens.add({
      targets: this,
      x: targetX,
      y: targetY,
      duration: 1500,
      ease: 'Sine.easeInOut',
      onComplete: () => {
        // Show happy emoji
        const heart = this.scene.add.text(this.x, this.y - 30, '💕', {
          fontSize: '16px'
        }).setOrigin(0.5).setDepth(1000);
        
        this.scene.tweens.add({
          targets: heart,
          y: heart.y - 20,
          alpha: 0,
          duration: 1000,
          onComplete: () => heart.destroy()
        });
      }
    });
  }

  destroy(fromScene?: boolean): void {
    if (this.wanderTween) this.wanderTween.stop();
    if (this.bobTween) this.bobTween.stop();
    if (this.popTween) this.popTween.stop();
    if (this.interactionTimer) this.interactionTimer.destroy();
    super.destroy(fromScene);
  }
}
